import React, { useState, useEffect } from 'react';
import { Upload, Trash2, Check, FileText, FolderOpen, Link2, ChevronDown, ChevronUp, Layers } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { authFetch } from '../lib/authFetch';
import { toast } from 'sonner';

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'ru', name: 'Russian' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'fr', name: 'French' },
  { code: 'it', name: 'Italian' },
  { code: 'es', name: 'Spanish' },
  { code: 'ko', name: 'Korean' },
];

const LANG_MAP = {
  english: 'en', russian: 'ru', ukrainian: 'uk', portuguese: 'pt',
  french: 'fr', italian: 'it', spanish: 'es', korean: 'ko',
  en: 'en', ru: 'ru', uk: 'uk', pt: 'pt', fr: 'fr', it: 'it', es: 'es', ko: 'ko',
};

const CATEGORIES = [
  { value: 'ARPWeek', label: 'ARP Reading (ARPWeek)' },
  { value: 'LessonWeek', label: 'Lesson (LessonWeek)' },
];

function DocumentLibrary() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [expandedWeeks, setExpandedWeeks] = useState({});

  // Add form state
  const [newCategory, setNewCategory] = useState('ARPWeek');
  const [newWeek, setNewWeek] = useState(0);
  const [newLanguage, setNewLanguage] = useState('en');
  const [newUrl, setNewUrl] = useState('');
  const [newFilename, setNewFilename] = useState('');

  // Bulk import state
  const [bulkText, setBulkText] = useState('');
  const [bulkParsed, setBulkParsed] = useState([]);
  const [bulkImporting, setBulkImporting] = useState(false);

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/document-library`);
      if (res.ok) setDocuments(await res.json());
    } catch (e) {
      toast.error('Failed to load document library');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newUrl.trim()) { toast.error('URL is required'); return; }
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/document-library`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: newCategory,
          week: parseInt(newWeek),
          language: newLanguage,
          url: newUrl.trim(),
          filename: newFilename.trim() || `${newCategory}${newWeek}-${newLanguage}`,
        }),
      });
      if (res.ok) {
        toast.success('Document added to library');
        setNewUrl('');
        setNewFilename('');
        setShowAddForm(false);
        fetchDocuments();
      }
    } catch (e) {
      toast.error('Failed to add document');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Remove this document from the library?')) return;
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/document-library/${docId}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Document removed'); fetchDocuments(); }
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleAssign = async () => {
    if (selectedDocs.length === 0) { toast.error('Select documents to assign'); return; }
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/document-library/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ doc_ids: selectedDocs }),
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Assigned ${result.assigned_count} document(s) to readings/lessons`);
        setSelectedDocs([]);
        fetchDocuments();
      }
    } catch (e) {
      toast.error('Failed to assign documents');
    }
  };

  // ===== BULK IMPORT FUNCTIONS =====
  const detectFromFilename = (url) => {
    // Extract filename from URL (decode URL-encoded parts)
    const decoded = decodeURIComponent(url);
    const filename = decoded.split('/').pop().replace(/\.[^.]+$/, ''); // strip extension

    let category = 'ARPWeek';
    let week = 0;
    let language = 'en';

    // Detect category: Lesson vs ARP
    if (/lesson/i.test(filename)) {
      category = 'LessonWeek';
    } else {
      category = 'ARPWeek';
    }

    // Detect week number: "Week5", "Week 12", "Week-3"
    const weekMatch = filename.match(/week[\s_-]*(\d+)/i);
    if (weekMatch) {
      week = parseInt(weekMatch[1]);
    } else if (/introduction|intro/i.test(filename)) {
      week = 0;
    }

    // Detect language from filename
    const lowerName = filename.toLowerCase();
    for (const [key, code] of Object.entries(LANG_MAP)) {
      if (key.length > 2 && lowerName.includes(key)) {
        language = code;
        break;
      }
    }

    return { category, week, language, filename: filename.replace(/^[a-z0-9]+_/i, '') };
  };

  const parseBulkUrls = (text) => {
    const lines = text.split('\n').map(l => l.trim()).filter(l => l && l.startsWith('http'));
    return lines.map(url => {
      const detected = detectFromFilename(url);
      return { url, ...detected, id: Math.random().toString(36).slice(2, 8) };
    });
  };

  const handleBulkParse = () => {
    const parsed = parseBulkUrls(bulkText);
    if (parsed.length === 0) { toast.error('No valid URLs found. Paste one URL per line.'); return; }
    setBulkParsed(parsed);
  };

  const updateBulkItem = (id, field, value) => {
    setBulkParsed(prev => prev.map(p => p.id === id ? { ...p, [field]: field === 'week' ? parseInt(value) : value } : p));
  };

  const removeBulkItem = (id) => {
    setBulkParsed(prev => prev.filter(p => p.id !== id));
  };

  const handleBulkImport = async () => {
    if (bulkParsed.length === 0) return;
    setBulkImporting(true);
    let successCount = 0;
    for (const item of bulkParsed) {
      try {
        const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/document-library`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            category: item.category,
            week: item.week,
            language: item.language,
            url: item.url,
            filename: item.filename || `${item.category}${item.week}-${item.language}`,
          }),
        });
        if (res.ok) successCount++;
      } catch (e) { /* continue */ }
    }
    toast.success(`Imported ${successCount} of ${bulkParsed.length} documents`);
    setBulkText('');
    setBulkParsed([]);
    setShowBulkImport(false);
    setBulkImporting(false);
    fetchDocuments();
  };

  const toggleSelect = (docId) => {
    setSelectedDocs(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const toggleWeek = (key) => {
    setExpandedWeeks(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Group documents by category+week
  const grouped = {};
  documents.forEach(doc => {
    const key = `${doc.category}-${doc.week}`;
    if (!grouped[key]) grouped[key] = { category: doc.category, week: doc.week, docs: [] };
    grouped[key].docs.push(doc);
  });
  const groupedKeys = Object.keys(grouped).sort((a, b) => {
    const [catA, weekA] = a.split('-');
    const [catB, weekB] = b.split('-');
    if (catA !== catB) return catA.localeCompare(catB);
    return parseInt(weekA) - parseInt(weekB);
  });

  const weekLabel = (category, week) => {
    const prefix = category === 'ARPWeek' ? 'ARP Reading' : 'Lesson';
    return week === 0 ? `${prefix}: Introduction` : `${prefix}: Week ${week}`;
  };

  const unassignedCount = documents.filter(d => !d.assigned).length;

  if (loading) return <div className="text-center py-8 text-zinc-500">Loading document library...</div>;

  return (
    <div className="space-y-4" data-testid="document-library">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-zinc-900">Document Library</h3>
          <p className="text-xs text-zinc-500">{documents.length} documents, {unassignedCount} unassigned</p>
        </div>
        <div className="flex gap-2">
          {unassignedCount > 0 && selectedDocs.length > 0 && (
            <Button
              onClick={handleAssign}
              className="bg-green-600 hover:bg-green-700 text-white text-xs h-8"
              data-testid="assign-docs-btn"
            >
              <Check className="w-3 h-3 mr-1" /> Assign ({selectedDocs.length})
            </Button>
          )}
          <Button
            onClick={() => { setShowBulkImport(!showBulkImport); setShowAddForm(false); }}
            className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-8"
            data-testid="bulk-import-btn"
          >
            <Layers className="w-3 h-3 mr-1" /> Bulk Import
          </Button>
          <Button
            onClick={() => { setShowAddForm(!showAddForm); setShowBulkImport(false); }}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8"
            data-testid="add-document-btn"
          >
            <Upload className="w-3 h-3 mr-1" /> Add Document
          </Button>
        </div>
      </div>

      {/* Naming Convention Guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-xs font-semibold text-blue-800 mb-1">Naming Convention</p>
        <p className="text-xs text-blue-700">
          <strong>Readings:</strong> ARPWeek0 (Intro), ARPWeek1, ARPWeek2... ARPWeek30<br/>
          <strong>Lessons:</strong> LessonWeek0 (Intro), LessonWeek1, LessonWeek2... LessonWeek30<br/>
          Upload the PDF via Emergent chat, then paste the URL here with the correct category, week and language.
        </p>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3" data-testid="add-document-form">
          <h4 className="text-sm font-bold text-zinc-900">Register New Document</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-zinc-600 block mb-1">Category</label>
              <select
                value={newCategory}
                onChange={e => setNewCategory(e.target.value)}
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm"
                data-testid="doc-category-select"
              >
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600 block mb-1">Week (0 = Intro)</label>
              <select
                value={newWeek}
                onChange={e => setNewWeek(e.target.value)}
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm"
                data-testid="doc-week-select"
              >
                <option value={0}>0: Introduction</option>
                {Array.from({ length: 30 }, (_, i) => i + 1).map(w => (
                  <option key={w} value={w}>{w}: Week {w}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600 block mb-1">Language</label>
              <select
                value={newLanguage}
                onChange={e => setNewLanguage(e.target.value)}
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm"
                data-testid="doc-language-select"
              >
                {LANGUAGES.map(l => (
                  <option key={l.code} value={l.code}>{l.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600 block mb-1">Filename (optional)</label>
              <Input
                value={newFilename}
                onChange={e => setNewFilename(e.target.value)}
                placeholder="e.g. ARPWeek1-English"
                className="text-sm"
                data-testid="doc-filename-input"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600 block mb-1">PDF URL (from Emergent upload)</label>
            <Input
              value={newUrl}
              onChange={e => setNewUrl(e.target.value)}
              placeholder="https://customer-assets.emergentagent.com/..."
              className="text-sm"
              data-testid="doc-url-input"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={handleAdd} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8" data-testid="save-document-btn">
              Save Document
            </Button>
            <Button onClick={() => setShowAddForm(false)} variant="outline" className="text-xs h-8">Cancel</Button>
          </div>
        </div>
      )}

      {/* Bulk Import Form */}
      {showBulkImport && (
        <div className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3" data-testid="bulk-import-form">
          <h4 className="text-sm font-bold text-zinc-900">Bulk Import Documents</h4>
          <p className="text-xs text-zinc-500">Paste multiple PDF URLs below (one per line). The system will auto-detect category, week and language from the filename.</p>

          {bulkParsed.length === 0 ? (
            <>
              <textarea
                value={bulkText}
                onChange={e => setBulkText(e.target.value)}
                rows={6}
                className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-400 resize-y"
                placeholder={"https://customer-assets.emergentagent.com/job.../ARPWeek1English.pdf\nhttps://customer-assets.emergentagent.com/job.../ARPWeek1Russian.pdf\nhttps://customer-assets.emergentagent.com/job.../LessonWeek2French.pdf"}
                data-testid="bulk-urls-textarea"
              />
              <div className="flex gap-2">
                <Button onClick={handleBulkParse} className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-8" data-testid="bulk-parse-btn">
                  Parse URLs ({bulkText.split('\n').filter(l => l.trim().startsWith('http')).length})
                </Button>
                <Button onClick={() => setShowBulkImport(false)} variant="outline" className="text-xs h-8">Cancel</Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-amber-700 font-medium">Review {bulkParsed.length} detected documents. Adjust category, week or language if needed.</p>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {bulkParsed.map(item => (
                  <div key={item.id} className="border border-zinc-200 rounded-lg p-3 space-y-2" data-testid={`bulk-item-${item.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-500 truncate flex-1 font-mono" title={item.url}>
                        {item.filename || item.url.split('/').pop()}
                      </span>
                      <button onClick={() => removeBulkItem(item.id)} className="text-red-400 hover:text-red-600 ml-2">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <select
                        value={item.category}
                        onChange={e => updateBulkItem(item.id, 'category', e.target.value)}
                        className="border border-zinc-300 rounded px-2 py-1 text-xs"
                      >
                        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                      <select
                        value={item.week}
                        onChange={e => updateBulkItem(item.id, 'week', e.target.value)}
                        className="border border-zinc-300 rounded px-2 py-1 text-xs"
                      >
                        <option value={0}>Week 0 (Intro)</option>
                        {Array.from({ length: 30 }, (_, i) => i + 1).map(w => (
                          <option key={w} value={w}>Week {w}</option>
                        ))}
                      </select>
                      <select
                        value={item.language}
                        onChange={e => updateBulkItem(item.id, 'language', e.target.value)}
                        className="border border-zinc-300 rounded px-2 py-1 text-xs"
                      >
                        {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  onClick={handleBulkImport}
                  disabled={bulkImporting}
                  className="bg-green-600 hover:bg-green-700 text-white text-xs h-8"
                  data-testid="bulk-import-confirm-btn"
                >
                  {bulkImporting ? 'Importing...' : `Import All (${bulkParsed.length})`}
                </Button>
                <Button onClick={() => setBulkParsed([])} variant="outline" className="text-xs h-8">Back to Edit</Button>
                <Button onClick={() => { setShowBulkImport(false); setBulkParsed([]); setBulkText(''); }} variant="outline" className="text-xs h-8">Cancel</Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Document Groups */}
      {groupedKeys.length === 0 ? (
        <div className="text-center py-12 text-zinc-400">
          <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No documents in the library yet.</p>
          <p className="text-xs mt-1">Upload PDFs via Emergent chat, then register them here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {groupedKeys.map(key => {
            const group = grouped[key];
            const isExpanded = expandedWeeks[key] !== false; // default open
            const assignedInGroup = group.docs.filter(d => d.assigned).length;
            return (
              <div key={key} className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleWeek(key)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50"
                  data-testid={`doc-group-${key}`}
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-semibold text-zinc-900">{weekLabel(group.category, group.week)}</span>
                    <span className="text-xs text-zinc-400">
                      {group.docs.length} file{group.docs.length !== 1 ? 's' : ''}
                      {assignedInGroup > 0 && ` (${assignedInGroup} assigned)`}
                    </span>
                  </div>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
                </button>
                {isExpanded && (
                  <div className="border-t border-zinc-100 divide-y divide-zinc-100">
                    {group.docs.map(doc => (
                      <div key={doc.doc_id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                        {!doc.assigned && (
                          <input
                            type="checkbox"
                            checked={selectedDocs.includes(doc.doc_id)}
                            onChange={() => toggleSelect(doc.doc_id)}
                            className="w-4 h-4 accent-blue-600"
                            data-testid={`doc-check-${doc.doc_id}`}
                          />
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          doc.assigned ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {doc.assigned ? 'Assigned' : 'Pending'}
                        </span>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                          {doc.language_name || doc.language}
                        </span>
                        <span className="text-zinc-600 truncate flex-1" title={doc.url}>
                          {doc.filename || doc.url.split('/').pop()}
                        </span>
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                          title="Open PDF"
                        >
                          <Link2 className="w-3.5 h-3.5" />
                        </a>
                        <button
                          onClick={() => handleDelete(doc.doc_id)}
                          className="text-red-400 hover:text-red-600"
                          data-testid={`delete-doc-${doc.doc_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default DocumentLibrary;
